import { AnimatedSprite, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../../data/constants';

// Night sky seen through the broken wall: starry backdrop (area-top-hole-bg),
// the animated moon, and clouds drifting both BEHIND and IN FRONT of it. The
// whole stack mounts UNDER the wall art (area_top_hole.png) - everything
// outside the hole is occluded by the wall, so clouds can roam a band wider
// than the hole and just slide in and out behind its broken edge.

// Moon placement inside the hole (the hole in the art is right of center,
// roughly x 545-825, y 30-270 design px). Sits up-right of the hole center.
// Height in design px, aspect kept. Exported - Moonlight.ts anchors the beam
// mouth to the moon so the light falls FROM it.
export const MOON = { x: 730, y: 125, height: 120 };
// AnimatedSprite frames/tick on the shared (battle-pace-scaled) ticker - same
// convention as the torch flame.
const MOON_ANIM_SPEED = 0.5;
// The moon idles on frame 0 and plays its sheet ONCE at random wall-time
// intervals (a rare wink, not a constant loop).
const MOON_IDLE_MIN_SEC = 5;
const MOON_IDLE_MAX_SEC = 15;
// Gentle vertical bob around MOON.y - wall-time sine so the sway keeps its
// pace regardless of the battle slow-mo. The period re-rolls within its range
// after every full cycle, so the tempo wanders a little instead of ticking
// like a metronome.
const MOON_BOB_AMPLITUDE = 6;
const MOON_BOB_MIN_PERIOD_SEC = 7;
const MOON_BOB_MAX_PERIOD_SEC = 12;
// Contour glow: a blurred additive copy of the moon frame behind the disc,
// scaled up a touch - hugs the crescent silhouette instead of a round halo.
// Blur is in design px; baked once into a canvas texture (see bakeMoonGlow).
const MOON_GLOW_SCALE = 1.06;
const MOON_GLOW_BLUR = 10;
const MOON_GLOW_ALPHA = 0.8;
// Supersample for the glow bake, same idea as the beam bake.
const GLOW_BAKE_SCALE = 2;

// Cloud band and per-cloud randomization ranges. One source texture; every
// (re)spawn rolls its own size / alpha / height / speed so no two passes look
// the same. Speed is design-px per SECOND of wall time - ambient drift must
// not slow down with the battle slow-mo, hence the performance.now clock.
const CLOUD_BAND = { left: 470, right: 890, top: 55, bottom: 235 };
const CLOUD_COUNT_BACK = 4;
const CLOUD_COUNT_FRONT = 6;
// Front clouds run bigger than back ones - cheap depth cue and keeps the
// foreground busy.
const CLOUD_BACK_MIN_WIDTH = 90;
const CLOUD_BACK_MAX_WIDTH = 150;
const CLOUD_FRONT_MIN_WIDTH = 130;
const CLOUD_FRONT_MAX_WIDTH = 210;
// Alpha is a 50/50 coin per spawn: either a solid cloud or a translucent one
// (a uniform ramp made almost everything read as see-through).
const CLOUD_SOLID_CHANCE = 0.5;
const CLOUD_TRANSLUCENT_MIN_ALPHA = 0.35;
const CLOUD_TRANSLUCENT_MAX_ALPHA = 0.55;
const CLOUD_MIN_SPEED = 6;
const CLOUD_MAX_SPEED = 16;
// Clamp per-frame dt so a suspended tab doesn't teleport clouds on resume.
const MAX_FRAME_DT_SEC = 0.5;

// Snow (optional, new-year vibe): simple white dots falling through the hole
// band, in front of the moon and clouds. Same wall-time clock as the clouds;
// everything outside the hole is hidden by the wall art.
const SNOW_BAND = { left: 470, right: 890, top: 25, bottom: 250 };
const SNOW_COUNT = 40;
const SNOW_MIN_RADIUS = 1;
const SNOW_MAX_RADIUS = 2.5;
const SNOW_MIN_ALPHA = 0.5;
const SNOW_MAX_ALPHA = 1;
// Fall speed in design-px per second of wall time; drift is the horizontal
// wobble range (+- per flake).
const SNOW_MIN_FALL_SPEED = 15;
const SNOW_MAX_FALL_SPEED = 40;
const SNOW_MAX_DRIFT_SPEED = 8;

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

// Bakes the glow once: the moon frame drawn into a canvas at display scale
// and blurred THERE (ctx.filter). A runtime BlurFilter rendered its temp
// buffer as a faint rectangle over the dark sky (kawase passes leave 1-2/255
// alpha noise across the whole buffer, and additive blending lights it up);
// the baked canvas has true-zero pixels outside the halo, so no rectangle.
// Returns null when the frame's source or the 2d context is unavailable -
// callers just skip the glow.
function bakeMoonGlow(frame: Texture): Texture | null {
  const source = frame.source.resource as CanvasImageSource | undefined;
  if (!source) return null;
  const region = frame.frame;
  if (region.width <= 0 || region.height <= 0) return null;
  const displayHeight = MOON.height * MOON_GLOW_SCALE;
  const displayWidth = (region.width / region.height) * displayHeight;
  // Padding gives the blur room to decay to zero inside the texture.
  const pad = MOON_GLOW_BLUR * 3;
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil((displayWidth + pad * 2) * GLOW_BAKE_SCALE);
  canvas.height = Math.ceil((displayHeight + pad * 2) * GLOW_BAKE_SCALE);
  const c2d = canvas.getContext('2d');
  if (!c2d) return null;
  c2d.filter = `blur(${MOON_GLOW_BLUR * GLOW_BAKE_SCALE}px)`;
  c2d.drawImage(
    source,
    region.x,
    region.y,
    region.width,
    region.height,
    pad * GLOW_BAKE_SCALE,
    pad * GLOW_BAKE_SCALE,
    displayWidth * GLOW_BAKE_SCALE,
    displayHeight * GLOW_BAKE_SCALE,
  );
  return Texture.from(canvas);
}

type CloudState = { sprite: Sprite; speed: number; front: boolean };

type SnowflakeState = { dot: Graphics; fallSpeed: number; driftSpeed: number };

export type NightSkyOptions = {
  sky?: Texture;
  moonFrames?: Texture[];
  cloud?: Texture;
  snow?: boolean;
};

export class NightSky extends Container {
  private readonly clouds: CloudState[] = [];
  private readonly snowflakes: SnowflakeState[] = [];
  private moon: AnimatedSprite | null = null;
  // Moon disc + its glow halo; this is what bobs, so the glow rides along.
  private moonGroup: Container | null = null;
  // Bob state: phase accumulates by the current period; a fresh period is
  // rolled after each full cycle (see MOON_BOB_* constants).
  private bobPhase = 0;
  private bobPeriodSec = randomBetween(MOON_BOB_MIN_PERIOD_SEC, MOON_BOB_MAX_PERIOD_SEC);
  private nextMoonPlayAt =
    performance.now() + randomBetween(MOON_IDLE_MIN_SEC, MOON_IDLE_MAX_SEC) * 1000;
  private lastTime = performance.now();

  constructor(opts: NightSkyOptions) {
    super();

    if (opts.sky) {
      const bg = new Sprite(opts.sky);
      bg.width = DESIGN_WIDTH;
      bg.height = DESIGN_HEIGHT;
      this.addChild(bg);
    }

    const backClouds = new Container();
    this.addChild(backClouds);

    if (opts.moonFrames && opts.moonFrames.length > 0) {
      const moonGroup = new Container();
      moonGroup.position.set(MOON.x, MOON.y);

      const firstFrame = opts.moonFrames[0];
      const moonFitScale = firstFrame ? MOON.height / firstFrame.height : 1;

      // Glow behind the disc: the moon's own silhouette, blurred and additive,
      // slightly upscaled - light traces the crescent contour exactly.
      if (firstFrame) {
        const glowTexture = bakeMoonGlow(firstFrame);
        if (glowTexture) {
          const glow = new Sprite(glowTexture);
          glow.anchor.set(0.5);
          glow.scale.set(1 / GLOW_BAKE_SCALE);
          glow.alpha = MOON_GLOW_ALPHA;
          glow.blendMode = 'add';
          moonGroup.addChild(glow);
        }
      }

      const moon = new AnimatedSprite(opts.moonFrames);
      moon.anchor.set(0.5);
      moon.scale.set(moonFitScale);
      moon.animationSpeed = MOON_ANIM_SPEED;
      // Idle still on frame 0; update() kicks off a one-shot pass at random
      // intervals and this snaps it back to the idle frame when it ends.
      moon.loop = false;
      moon.gotoAndStop(0);
      moon.onComplete = () => moon.gotoAndStop(0);
      moonGroup.addChild(moon);

      this.addChild(moonGroup);
      this.moon = moon;
      this.moonGroup = moonGroup;
    }

    const frontClouds = new Container();
    this.addChild(frontClouds);

    if (opts.cloud) {
      for (let i = 0; i < CLOUD_COUNT_BACK + CLOUD_COUNT_FRONT; i++) {
        const sprite = new Sprite(opts.cloud);
        sprite.anchor.set(0.5);
        const front = i >= CLOUD_COUNT_BACK;
        const state: CloudState = { sprite, speed: 0, front };
        // Initial pass spawns anywhere in the band so the sky isn't empty on
        // the first frames; respawns enter from the left edge.
        this.randomizeCloud(state, false);
        (front ? frontClouds : backClouds).addChild(sprite);
        this.clouds.push(state);
      }
    }

    if (opts.snow) {
      const snowLayer = new Container();
      this.addChild(snowLayer);
      for (let i = 0; i < SNOW_COUNT; i++) {
        const flake: SnowflakeState = { dot: new Graphics(), fallSpeed: 0, driftSpeed: 0 };
        // Initial pass fills the whole band; respawns re-enter from the top.
        this.randomizeSnowflake(flake, false);
        snowLayer.addChild(flake.dot);
        this.snowflakes.push(flake);
      }
    }

    // Per-frame drift only while in the rendered tree - stops on destroy, same
    // pattern as the torch flicker.
    this.onRender = () => this.update();
  }

  // Current vertical bob offset of the moon (0 when centered). Moonlight's
  // beam reads this each frame to sway together with the moon.
  get bobOffset(): number {
    return this.moonGroup ? this.moonGroup.y - MOON.y : 0;
  }

  private randomizeCloud(cloud: CloudState, enterFromLeft: boolean): void {
    const { sprite } = cloud;
    const width = cloud.front
      ? randomBetween(CLOUD_FRONT_MIN_WIDTH, CLOUD_FRONT_MAX_WIDTH)
      : randomBetween(CLOUD_BACK_MIN_WIDTH, CLOUD_BACK_MAX_WIDTH);
    const textureWidth = sprite.texture.width;
    if (textureWidth > 0) sprite.scale.set(width / textureWidth);
    sprite.alpha =
      Math.random() < CLOUD_SOLID_CHANCE
        ? 1
        : randomBetween(CLOUD_TRANSLUCENT_MIN_ALPHA, CLOUD_TRANSLUCENT_MAX_ALPHA);
    sprite.y = randomBetween(CLOUD_BAND.top, CLOUD_BAND.bottom);
    sprite.x = enterFromLeft
      ? CLOUD_BAND.left - width / 2
      : randomBetween(CLOUD_BAND.left, CLOUD_BAND.right);
    cloud.speed = randomBetween(CLOUD_MIN_SPEED, CLOUD_MAX_SPEED);
  }

  private randomizeSnowflake(flake: SnowflakeState, enterFromTop: boolean): void {
    const radius = randomBetween(SNOW_MIN_RADIUS, SNOW_MAX_RADIUS);
    flake.dot.clear().circle(0, 0, radius).fill(0xffffff);
    flake.dot.alpha = randomBetween(SNOW_MIN_ALPHA, SNOW_MAX_ALPHA);
    flake.dot.x = randomBetween(SNOW_BAND.left, SNOW_BAND.right);
    flake.dot.y = enterFromTop
      ? SNOW_BAND.top
      : randomBetween(SNOW_BAND.top, SNOW_BAND.bottom);
    flake.fallSpeed = randomBetween(SNOW_MIN_FALL_SPEED, SNOW_MAX_FALL_SPEED);
    flake.driftSpeed = randomBetween(-SNOW_MAX_DRIFT_SPEED, SNOW_MAX_DRIFT_SPEED);
  }

  private update(): void {
    const now = performance.now();
    const dtSec = Math.min((now - this.lastTime) / 1000, MAX_FRAME_DT_SEC);
    this.lastTime = now;
    for (const cloud of this.clouds) {
      cloud.sprite.x += cloud.speed * dtSec;
      if (cloud.sprite.x - cloud.sprite.width / 2 > CLOUD_BAND.right) {
        this.randomizeCloud(cloud, true);
      }
    }
    for (const flake of this.snowflakes) {
      flake.dot.y += flake.fallSpeed * dtSec;
      flake.dot.x += flake.driftSpeed * dtSec;
      if (flake.dot.y > SNOW_BAND.bottom) {
        this.randomizeSnowflake(flake, true);
      }
    }
    if (this.moonGroup) {
      this.bobPhase += (dtSec / this.bobPeriodSec) * Math.PI * 2;
      if (this.bobPhase >= Math.PI * 2) {
        this.bobPhase -= Math.PI * 2;
        this.bobPeriodSec = randomBetween(MOON_BOB_MIN_PERIOD_SEC, MOON_BOB_MAX_PERIOD_SEC);
      }
      this.moonGroup.y = MOON.y + Math.sin(this.bobPhase) * MOON_BOB_AMPLITUDE;
    }
    if (this.moon) {
      if (!this.moon.playing && now >= this.nextMoonPlayAt) {
        this.moon.gotoAndPlay(0);
        this.nextMoonPlayAt =
          now + randomBetween(MOON_IDLE_MIN_SEC, MOON_IDLE_MAX_SEC) * 1000;
      }
    }
  }
}
